import { TRACKER_TYPES } from '@racemap/sdk/schema/trackers';
import { Select } from 'antd';
import type { FC } from 'react';
import { FormLabel } from 'react-bootstrap';
import { useCurrentUser } from '../../../lib/customHooks';
import trackerTypes from '../../../misc/tracker_types';
import { VStack } from '../../BasicComponents/MetaComponent';

const { Option, OptGroup } = Select;

interface Props {
  value: TRACKER_TYPES;
  onChange: (tracker: TRACKER_TYPES) => void;
  disabled?: boolean;
}

export const TrackerTypeSelect: FC<Props> = ({ value, onChange, disabled }) => {
  const user = useCurrentUser();
  if (user == null) return null;
  const userIsRRCustomer = user.integrations?.raceResultCustomerId !== null;
  const userIsInsidersCustomer = user.integrations?.insidersAccessToken !== null;

  const trackerOptions = Object.entries(trackerTypes)
    .filter(([_, options]) => !options.isRRDevice)
    .filter(([_, options]) => !options.isInsidersDevice)
    .filter(
      ([deviceType]) =>
        deviceType !== TRACKER_TYPES.RacemapTracker &&
        deviceType !== TRACKER_TYPES.RMSportTrackerMk0,
    )
    .map(([deviceType]) => (
      <Option key={deviceType} value={deviceType}>
        {deviceType}
      </Option>
    ));
  const rrDeviceOptions = [
    TRACKER_TYPES.RRDecoder,
    TRACKER_TYPES.RRTrackbox,
    TRACKER_TYPES.RRUbidium,
  ].map((deviceType) => (
    <Option key={deviceType} value={deviceType}>
      {getRRDeviceLabel(deviceType)}
    </Option>
  ));

  const insidersDeviceOptions = [
    TRACKER_TYPES.InsidersInspirit,
    TRACKER_TYPES.InsidersIntense,
    TRACKER_TYPES.InsidersSimulator,
  ].map((deviceType) => (
    <Option key={deviceType} value={deviceType}>
      {getInsidersDeviceLabel(deviceType)}
    </Option>
  ));

  return (
    <VStack justify="flex-start">
      <FormLabel>Tracker Type:</FormLabel>
      <Select value={value} style={{ minWidth: 200 }} onChange={onChange} disabled={disabled}>
        {userIsRRCustomer || userIsInsidersCustomer ? (
          <>
            <OptGroup label="Tracker">{trackerOptions}</OptGroup>
            {userIsRRCustomer && <OptGroup label="Race|Result Devices">{rrDeviceOptions}</OptGroup>}
            {userIsInsidersCustomer && (
              <OptGroup label="Insiders Devices">{insidersDeviceOptions}</OptGroup>
            )}
          </>
        ) : (
          trackerOptions
        )}
      </Select>
    </VStack>
  );
};

const getRRDeviceLabel = (deviceType: TRACKER_TYPES): string => {
  switch (deviceType) {
    case TRACKER_TYPES.RRDecoder:
      return 'Decoder';
    case TRACKER_TYPES.RRTrackbox:
      return 'Trackbox';
    case TRACKER_TYPES.RRUbidium:
      return 'Ubibium';
    default:
      return '';
  }
};

const getInsidersDeviceLabel = (deviceType: TRACKER_TYPES): string => {
  switch (deviceType) {
    case TRACKER_TYPES.InsidersInspirit:
      return 'Inspirit';
    case TRACKER_TYPES.InsidersIntense:
      return 'Intense';
    case TRACKER_TYPES.InsidersSimulator:
      return 'Simulator';
    default:
      return '';
  }
};
